import React from "react";
import { App as AntdApp, Tag, Tooltip } from "antd";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery } from "@apollo/client";
import { Controller } from "react-hook-form";
import { ColumnsType } from "antd/es/table";
import { GET_DEALS, CREATE_DEAL } from "../../../utils/api/preferredDeal";
import { GET_ALL_WEBSITES_AND_TEMPLATES } from "../../../utils/api/placement";
import { useModal, useQueryVariables } from "../../../utils/hooks";
import Table from "../../../components/lists/Table/Table";
import SettingsCard from "./SettingsCard";
import FormModal from "../../../components/modals/FormModal";
import SingleLineField from "../../../components/forms/SingleLineField";
import DropdownField from "../../../components/forms/DropdownField";
import FormItem from "../../../components/forms/utils/FormItem";
import InputNumber from "../../../components/forms/InputNumber";
import Select from "../../../components/forms/Select";
import { DateCell } from "../../../components/lists/Table/shared/DateCell";
import { Deal, DealCampaignType, DealStatus } from "../../../__generated__/graphql";

const getColumns = (onCopyLink: (link: string) => void): ColumnsType<Deal> => [
  {
    key: "id",
    title: "ID",
    dataIndex: "_id",
    render: (id: string) => (
      <Tooltip title={id}>
        <span className="text-sm font-mono">{id.slice(-8)}</span>
      </Tooltip>
    ),
  },
  {
    key: "buyer",
    title: "Buyer",
    dataIndex: "buyer",
    render: (buyer: any) => buyer?.name || "-",
  },
  {
    key: "advertiser",
    title: "Advertiser",
    dataIndex: "advertiser",
    render: (advertiser: any) => advertiser?.name || "-",
  },
  {
    key: "status",
    title: "Status",
    dataIndex: "status",
    render: (status: DealStatus) => (
      <Tag color={status === DealStatus.Used ? "green" : "blue"}>
        {status === DealStatus.Used ? "Used" : "Active"}
      </Tag>
    ),
  },
  {
    key: "revenue",
    title: "Revenue",
    dataIndex: "revenue",
    render: (value: number) => (
      <span className="text-sm">
        {value != null ? `${Number(value).toLocaleString("en-US")} DKK` : "-"}
      </span>
    ),
  },
  {
    key: "campaignType",
    title: "Type",
    dataIndex: "campaignType",
    render: (type: DealCampaignType) =>
      type === DealCampaignType.Display ? "Display" : "Content",
  },
  {
    key: "createdAt",
    title: "Created at",
    dataIndex: "createdAt",
    render: (value: string) => (value ? <DateCell value={value} /> : "-"),
  },
  {
    key: "actions",
    title: "",
    render: (_: any, record: Deal) => (
      <div className="px-3">
        {record.campaignCreationLink && (
          <Tooltip title="Copy campaign creation link">
            <button
              className="p-1 text-gray-400 hover:text-gray-600 cursor-pointer"
              onClick={() => onCopyLink(record.campaignCreationLink!)}
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
            </button>
          </Tooltip>
        )}
      </div>
    ),
  },
];

const PreferredDeals: React.FC = () => {
  const { message } = AntdApp.useApp();
  const { variables, setPagination, setFilters } = useQueryVariables();
  const [modalVisible, showModal, hideModal] = useModal();

  const { loading, data } = useQuery(GET_DEALS, { variables });
  const { data: websitesData } = useQuery(GET_ALL_WEBSITES_AND_TEMPLATES);

  const [createDeal] = useMutation(CREATE_DEAL, {
    refetchQueries: ["DealMany"],
  });

  const deals = (data?.deals?.items || []) as Deal[];
  const websites = websitesData?.websites?.items || [];

  const handleCreate = async (form: any) => {
    await createDeal({
      variables: {
        record: {
          campaignType: form.campaignType,
          buyerId: form.buyerId,
          advertiserId: form.advertiserId,
          websiteIds: form.websiteIds,
          platformDiscount: form.platformDiscount / 100,
        },
      },
    });
  };

  const handleCopyLink = (link: string) => {
    navigator.clipboard.writeText(link);
    message.success("Campaign creation link copied to clipboard");
  };

  const columns = getColumns(handleCopyLink);

  const defaultWebsiteIds = websites.map((w: any) => w.value);

  return (
    <SettingsCard
      title="Preferred Deals"
      description="Create and manage preferred deals with buyers."
    >
      <Table
        className="mt-8"
        columns={columns}
        data={deals}
        actionButtonText="Add Preferred Deal"
        onActionButtonClick={showModal}
        rowKey="_id"
        // searchable={false}
        pagination={{
          total: data?.deals?.meta?.totalItems || 0,
          pageSize: variables.perPage,
          current: variables.page,
        }}
        onChange={(pagination, filters) => {
          setPagination(pagination);
          setFilters(filters);
        }}
        loading={loading}
      />

      <FormModal
        open={modalVisible}
        title="Add Preferred Deal"
        okText="Create"
        onCancel={hideModal}
        onOk={handleCreate}
        defaultValues={{ websiteIds: defaultWebsiteIds }}
      >
        <DropdownField
          top
          required
          label="Campaign Type"
          htmlfor="campaignType"
          options={[
            { label: "Content", value: DealCampaignType.Content },
            { label: "Display", value: DealCampaignType.Display },
          ]}
        />
        <SingleLineField
          label="Buyer Account ID"
          htmlfor="buyerId"
          required
        />
        <SingleLineField
          label="Advertiser ID"
          htmlfor="advertiserId"
          required
        />
        <FormItem label="Websites">
          <Controller
            name="websiteIds"
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <Select
                className="w-full sm:max-w-xs"
                placeholder="Select websites"
                mode="multiple"
                options={websites}
                allowSelectAll
                value={value}
                onChange={onChange}
                maxTagCount="responsive"
                optionFilterProp="label"
              />
            )}
          />
        </FormItem>
        <FormItem label="Platform Discount">
          <Controller
            name="platformDiscount"
            rules={{
              required: true,
              min: { value: 0, message: "Minimum is 0%" },
              max: { value: 20, message: "Maximum is 20%" },
            }}
            render={({ field }) => (
              <InputNumber
                className="w-full sm:max-w-xs"
                min={0}
                max={20}
                {...field}
                suffix="%"
              />
            )}
          />
        </FormItem>
      </FormModal>
    </SettingsCard>
  );
};

export default PreferredDeals;
